import { ElementType, Payrails, PayrailsEnvironment, type InitOptions } from "@payrails/web-sdk"
import { HttpClient } from "@/shared/lib/http-client"
import type { PayrailsTheme } from "../types"

type PayrailsClient = ReturnType<typeof Payrails.init>
type CollectContainer = ReturnType<PayrailsClient["collectContainer"]>
type SecureField = ReturnType<CollectContainer["createCollectElement"]>

/**
 * DOM ids the three secure fields mount into — the checkout sheet renders one slot
 * per field and the provider mounts into them by id.
 *
 * The form deliberately uses Payrails' COLLECT container (one small iframe PER FIELD)
 * instead of `cardForm()` (the whole form in ONE composable iframe). With the
 * composable form the "Expires on" + "CVC" row was laid out INSIDE the cross-origin
 * iframe, and iOS WebKit / in-app browsers (Instagram, …) intermittently collapsed
 * that row to zero height on a cold-cache first paint — only the card number showed
 * until a reload. With COLLECT the row is our own flexbox in the main document and
 * every field box has a fixed height, so there is nothing for the iframe to collapse.
 * Same pattern as origene/Mytraits, which shares this backend and has no such bug.
 */
export const PAYRAILS_FIELD_IDS = {
  cardNumber: "payrails-card-number",
  expiry: "payrails-card-expiry",
  cvc: "payrails-card-cvc",
} as const

/** One of the three secure card fields. */
export type PayrailsFieldKey = keyof typeof PAYRAILS_FIELD_IDS

/**
 * Focus + validity of a secure field, surfaced so the sheet can draw the field's
 * border in OUR DOM. The border/focus/invalid ring can't live inside the iframe:
 * the slot clips its rounded corners, shaving the border on the curves. So the
 * iframe is borderless and the wrapper draws the border, toggled by this state.
 */
export interface PayrailsFieldState {
  focused: boolean
  /** Field is empty — drives "Fill in the field" vs a plain red (invalid) border. */
  empty: boolean
  /** Field holds a complete, valid value — gates submit; complete ⇒ no error shown. */
  complete: boolean
}

/** Called on focus/blur/change of a field so the UI can restyle its wrapper. */
export type PayrailsFieldStateListener = (
  field: PayrailsFieldKey,
  state: PayrailsFieldState,
) => void

/** Shape of a secure field's `getState()` snapshot, narrowed to what we read. */
type CollectFieldState = { isEmpty?: boolean; isValid?: boolean; isComplete?: boolean }

/** Whether a field holds a complete, valid value. Prefers the SDK's own `isComplete`. */
function fieldStateComplete(s: CollectFieldState): boolean {
  if (typeof s.isComplete === "boolean") return s.isComplete
  return !s.isEmpty && Boolean(s.isValid)
}

class PayrailsProvider extends HttpClient {
  private payrailsClient: PayrailsClient | null = null
  private container: CollectContainer | null = null
  private fields: Array<{ key: PayrailsFieldKey; field: SecureField }> = []
  private warmPromise: Promise<void> | null = null
  private initPromise: Promise<void> | null = null

  constructor(baseURL: string) {
    super({ baseURL, headers: { "Content-Type": "text/plain" } })
  }

  async warm(): Promise<void> {
    if (this.payrailsClient) return
    if (this.warmPromise) return this.warmPromise

    this.warmPromise = (async () => {
      const initOptions = await this.post<InitOptions>("/init/session", "")
      this.payrailsClient = Payrails.init(initOptions, {
        environment: PayrailsEnvironment.PRODUCTION,
      })
    })()

    try {
      await this.warmPromise
    } catch (err) {
      this.warmPromise = null
      throw err
    }
  }

  async init(
    theme: PayrailsTheme = "default",
    onReady?: () => void,
    onFieldState?: PayrailsFieldStateListener,
  ): Promise<void> {
    if (this.container) return
    if (this.initPromise) return this.initPromise

    this.initPromise = (async () => {
      await this.warm()
      if (!this.payrailsClient) throw new Error("Payrails client missing after warm")

      // System font on purpose: loading a webfont INSIDE the secure iframes was one of
      // the cold-cache stressors of the first-paint bug (and origene does the same).
      // Labels are our own DOM, so the visually dominant text keeps the app font.
      const FONT = "-apple-system, BlinkMacSystemFont, system-ui, sans-serif"

      // Skyflow input style variants (base/focus/empty/complete/invalid) — applied
      // inside each field's iframe. Placeholder color must be set here via the JSS
      // pseudo selector; our CSS can't reach into the iframe.
      const defaultInput = {
        base: {
          width: "100%",
          height: "52px",
          boxSizing: "border-box",
          padding: "0 16px",
          fontFamily: FONT,
          fontSize: "16px",
          fontWeight: "500",
          color: "#0F1924",
          background: "#FFFFFF",
          border: "1px solid #E2E2E5",
          borderRadius: "8px",
          outline: "none",
          transition: "border-color 0.15s ease, box-shadow 0.15s ease",
        },
        focus: { border: "1px solid #18181B", boxShadow: "0 0 0 3px rgba(24,24,27,0.08)" },
        empty: { color: "#9A9AA0" },
        complete: { color: "#0F1924" },
        invalid: {
          border: "1px solid #DC2626",
          color: "#DC2626",
          boxShadow: "0 0 0 3px rgba(220, 38, 38, 0.10)",
        },
        cardIcon: { opacity: "0.7" },
      }

      // Dark theme matching the checkout sheet (Figma: Payment - Card).
      const darkInput = {
        base: {
          width: "100%",
          height: "48px",
          boxSizing: "border-box",
          padding: "0 12px",
          fontFamily: FONT,
          fontSize: "16px",
          fontWeight: "500",
          color: "#dfcbf5",
          // Solid fill covers the iframe's default white body. NO border/radius here:
          // the border is drawn by OUR slot wrapper (like the First/Last name inputs),
          // because a border inside the iframe gets clipped at the corners by the slot's
          // rounded overflow. Focus/invalid are reflected on the wrapper via field
          // events (see below), not with a border inside the iframe.
          background: "#1b152f",
          outline: "none",
          // Placeholder color — matches the First/Last name inputs
          // (`placeholder:text-purple-light/30`).
          "&::placeholder": { color: "rgba(223,203,245,0.3)" },
        },
        empty: { color: "rgba(223,203,245,0.3)" },
        complete: { color: "#dfcbf5" },
        // Keep only the text color for an invalid value; the red ring is the wrapper's.
        invalid: { color: "#ff537f" },
        // The design has no brand/card icons inside the fields.
        cardIcon: { display: "none" },
      }

      // "clean" used to be "default without SDK labels"; labels are always our own
      // DOM now, so it collapses into the default input styling.
      const inputStyles = theme === "dark" ? darkInput : defaultInput
      // The SDK renders its error text INSIDE each field's iframe, below the input,
      // and that area's document body paints white — visible as a white strip on the
      // dark sheet (the slots also clip to the input height). Hide it: invalid state
      // shows via the red border, and submit errors surface under the pay button.
      const errorTextStyles = { base: { display: "none" } }

      const container = this.payrailsClient.collectContainer({ containerType: "COLLECT" })

      const specs = [
        { key: "cardNumber", type: ElementType.CARD_NUMBER, placeholder: "XXXX XXXX XXXX XXXX", id: PAYRAILS_FIELD_IDS.cardNumber },
        { key: "expiry", type: ElementType.EXPIRATION_DATE, placeholder: "MM / YY", id: PAYRAILS_FIELD_IDS.expiry },
        { key: "cvc", type: ElementType.CVV, placeholder: "•••", id: PAYRAILS_FIELD_IDS.cvc },
      ] as const

      // Reveal only once EVERY field's iframe has actually rendered — the UI keeps its
      // covers up until then, hiding the iframes' brief white bootstrap paint.
      let readyCount = 0
      this.fields = specs.map(({ key, type, placeholder, id }) => {
        const field = container.createCollectElement({
          type,
          placeholder,
          required: true,
          enableCardIcon: false,
          inputStyles,
          errorTextStyles,
        })
        // Reflect focus + emptiness + completeness on the slot wrapper (which draws the
        // border). Errors are revealed only on submit (see CardPaymentSheet) — never
        // while typing or on blur — matching the design's Card_Payment_Errors state (all
        // fields flagged at once when Proceed is tapped on an empty form).
        let focused = false
        const emit = () => {
          const s = field.getState?.()
          const empty = s ? s.isEmpty : true
          const complete = s ? fieldStateComplete(s) : false
          onFieldState?.(key, { focused, empty, complete })
        }
        field.on("READY", () => {
          readyCount += 1
          if (readyCount === specs.length) onReady?.()
        })
        field.on("FOCUS", () => {
          focused = true
          emit()
        })
        field.on("BLUR", () => {
          focused = false
          emit()
        })
        field.on("CHANGE", () => emit())
        if (!field.mount) throw new Error("Payrails secure field lacks mount()")
        field.mount(`#${id}`)
        return { key, field }
      })

      this.container = container
    })()

    try {
      await this.initPromise
    } catch (err) {
      this.initPromise = null
      throw err
    }
  }

  async tokenize() {
    if (!this.container) {
      throw new Error("Payment form is still loading. Please wait.")
    }
    // storeInstrument vaults the card for the recurring charge — same flag origene
    // sends to this backend.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result: any = await this.container.tokenize({ storeInstrument: true })
    if (!result || result.error) {
      throw new Error(result?.error || "Tokenization failed")
    }
    const instrument = result.instrument ?? result
    if (!instrument?.id) throw new Error("No instrument returned from Payrails")
    return instrument as { id: string; paymentMethod?: string; data?: { network?: string } }
  }

  /**
   * Per-field validity snapshot for client-side validation BEFORE tokenize. `true`
   * means the field holds a complete, valid value. Lets the sheet flag empty/invalid
   * fields ("Fill in the field") without calling tokenize — which for an empty form
   * surfaces Payrails' raw "Error collecting data from the iframe!" as a toast.
   */
  validateFields(): Record<PayrailsFieldKey, boolean> {
    const result: Record<PayrailsFieldKey, boolean> = {
      cardNumber: false,
      expiry: false,
      cvc: false,
    }
    for (const { key, field } of this.fields) {
      const s = field.getState?.()
      result[key] = s ? fieldStateComplete(s) : false
    }
    return result
  }

  reset() {
    for (const { field } of this.fields) {
      try {
        field.unmount?.()
      } catch {
        // best-effort teardown — the sheet is leaving the tree anyway
      }
    }
    this.fields = []
    this.container = null
    this.payrailsClient = null
    this.warmPromise = null
    this.initPromise = null
  }
}

export const payrailsProvider = new PayrailsProvider(import.meta.env.VITE_PAYMENT_API_URL || "")

export const warmPayrails = () => payrailsProvider.warm()
export const initPayrails = (
  theme?: PayrailsTheme,
  onReady?: () => void,
  onFieldState?: PayrailsFieldStateListener,
) => payrailsProvider.init(theme, onReady, onFieldState)
export const tokenizeCard = () => payrailsProvider.tokenize()
export const validateCardFields = () => payrailsProvider.validateFields()
export const resetPayrails = () => payrailsProvider.reset()
